/* src/render/parcelHighlight.ts — the glowing outline under a selected building.
 *
 * When the inspector opens on a building, the player has to be able to see *which* building —
 * a panel of numbers with no anchor in the world is a panel about nothing. This draws a bright,
 * flat, translucent copy of the footprint just above the ground, so the selection reads at a
 * glance from any camera angle.
 *
 * Like the cart marker, it is fed lon/lat rather than a local position and re-derives its
 * vertices on every floating-origin rebase — a highlight that drifted off its building after a
 * pan would be worse than none.
 */

import {
  BufferAttribute,
  BufferGeometry,
  DoubleSide,
  Mesh,
  MeshBasicMaterial,
  type Scene,
} from 'three';
import earcut from 'earcut';
import { toLocal, type Origin } from '../map/originRebase.ts';

export interface ParcelHighlight {
  /** Show the given footprint (lon/lat ring), or hide when the ring is empty. */
  set: (ring: ReadonlyArray<readonly [number, number]>, origin: Origin) => void;
  /** Re-derive from the stored ring after a rebase. */
  rebuild: (origin: Origin) => void;
  hide: () => void;
  dispose: () => void;
}

/** A warm gold that exists nowhere in the city palette, so a highlight cannot be missed. */
const HIGHLIGHT_COLOUR = 0xffcf3f;
/** A hand above the ground and the road decals, so the fill is never z-fought by them. */
const LIFT_M = 0.6;

export function createParcelHighlight(scene: Scene): ParcelHighlight {
  const geometry = new BufferGeometry();
  const material = new MeshBasicMaterial({
    color: HIGHLIGHT_COLOUR,
    transparent: true,
    opacity: 0.42,
    side: DoubleSide,
    // It is an overlay, not a solid: writing depth would let it occlude the very building it is
    // meant to point at when the camera dips to street level.
    depthWrite: false,
  });
  const mesh = new Mesh(geometry, material);
  mesh.renderOrder = 3; // after the ground layers, so it composites on top of them
  mesh.frustumCulled = false;
  mesh.visible = false;
  scene.add(mesh);

  let ring: ReadonlyArray<readonly [number, number]> = [];

  const build = (origin: Origin): void => {
    if (ring.length < 3) {
      mesh.visible = false;
      return;
    }
    // Flatten to tile-local [x, z, …] and triangulate. earcut handles the concave L-shaped
    // footprints that a naive fan would fold in on themselves.
    const flat: number[] = [];
    for (const [lon, lat] of ring) {
      const [x, z] = toLocal(lon, lat, origin);
      flat.push(x, z);
    }
    const tri = earcut(flat, undefined, 2);
    if (tri.length === 0) {
      mesh.visible = false;
      return;
    }

    const positions = new Float32Array((flat.length / 2) * 3);
    for (let i = 0; i < flat.length / 2; i += 1) {
      positions[i * 3] = flat[i * 2] ?? 0;
      positions[i * 3 + 1] = LIFT_M;
      positions[i * 3 + 2] = flat[i * 2 + 1] ?? 0;
    }
    geometry.setAttribute('position', new BufferAttribute(positions, 3));
    geometry.setIndex(new BufferAttribute(new Uint32Array(tri), 1));
    geometry.computeBoundingSphere();
    mesh.visible = true;
  };

  return {
    set(next, origin): void {
      ring = next;
      build(origin);
    },
    rebuild(origin): void {
      if (mesh.visible || ring.length >= 3) build(origin);
    },
    hide(): void {
      ring = [];
      mesh.visible = false;
    },
    dispose(): void {
      scene.remove(mesh);
      geometry.dispose();
      material.dispose();
    },
  };
}
